import * as React from 'react';

import _ from 'underscore';

import type {CoreEntityType, FilterObject} from 'nutshell-core/types';
import {getDisplayName} from 'nutshell-core/utils';

import {EntityTable} from './entity-table';
import type {Row, ColumnWidths} from './entity-table';

type ExplodedEntitiesProps = {
    entityType: CoreEntityType;
    entities: {
        schema: any;
        columns: {
            selected: any;
            resetToDefault: () => any;
            setFields: () => any;
        }[];
        query: {
            audience: {
                selected: string | null | undefined;
            };
            sort: {
                toggleSort: () => void;
            };
            fields: any;
            filter: {
                selected: FilterObject[];
                removeAllFilters: () => void;
            };
        };
        toggleItem?: (rowId: string) => void;
        shiftSelectItem: (rowId: string) => void;
        startScrolling: () => void;
        stopScrolling: () => void;
        isSelected: (row: Row) => boolean;
        selectAllItems?: () => void;
        getRow: (index: number) => Row;
        filtering: boolean;
        loading: boolean;
        firstLoad: boolean;
        allSelected: boolean;
        hasError: boolean;
        count: number;
        unfilteredCount: number;
    };
    onResizeColumn?: (columnWidths: ColumnWidths) => void;
    includeColumnPicker?: boolean;
    rowHeight?: number;
    height: number;
    width: number;
    scrollToColumnIndex?: number;
};

/**
 * This higher-order component pulls out the properties and methods from
 * an `entities` object, which is created by Baconjs, and adds them directly
 * as props to the WrappedComponent.
 *
 * This allows us to pull the destructuring logic out of the WrappedComponent
 * and use that component directly without the `entities` prop.
 *
 * (For now, we are still passing along `entities`, because not all
 * sub-components have been refactored yet.)
 *
 * @param  {Component} WrappedComponent - A react component
 * @return {Component}                  - A component with props pulled out of entities
 */
// @ts-expect-error
function explodeEntities(WrappedComponent) {
    return class HOC extends React.Component<ExplodedEntitiesProps> {
        static displayName = `explodeEntities(${getDisplayName(WrappedComponent)})`;

        render() {
            const {entities, ...otherProps} = this.props;

            // Hoist selected fields, don't nest under entity type
            const columns = _.clone(entities.query.fields);
            columns.selected = columns.selected[this.props.entityType];

            return (
                <WrappedComponent
                    {...otherProps} // `entities` is not passed in to props
                    schema={entities.schema.propertyMap}
                    columns={columns}
                    filters={entities.query.filter}
                    sort={entities.query.sort}
                    onToggleSort={entities.query.sort.toggleSort}
                    getRow={entities.getRow}
                    checkRowSelected={entities.isSelected}
                    toggleItem={entities.toggleItem}
                    shiftSelectItem={entities.shiftSelectItem}
                    startScrolling={entities.startScrolling}
                    stopScrolling={entities.stopScrolling}
                    selectAllItems={entities.selectAllItems}
                    isFiltering={entities.filtering}
                    isLoading={entities.loading}
                    isFirstLoad={entities.firstLoad}
                    isAllSelected={entities.allSelected}
                    hasError={entities.hasError}
                    rowCount={entities.count}
                    unfilteredCount={entities.unfilteredCount}
                    audienceId={entities.query.audience.selected}
                />
            );
        }
    };
}

export const EntityTableWrapper = explodeEntities(EntityTable);
